import { sql } from "drizzle-orm";
import {
  check,
  index,
  integer,
  sqliteTable,
  text,
  uniqueIndex,
} from "drizzle-orm/sqlite-core";
import { timestamps } from "@/db/columns.helpers";
import { user } from "@/db/schema/auth";
import type { PaymentProvider, PaymentStatus } from "@/db/schema/payment";
import {
  type PromotionDiscountSnapshot,
  promotion,
  promotionCode,
} from "@/db/schema/promotion";
import type { RefundState } from "@/db/schema/refund";
import { venue, venueAddon, venueSpot } from "@/db/schema/venue";
import {
  venueExperience,
  venueExperienceTicket,
} from "@/db/schema/venue-experience";
import type { PricingSnapshot } from "@/lib/pricing/types";
import type { TaxSnapshot } from "@/lib/tax/types";

export const VENUE_BOOKING_STATUS = [
  "pending",
  "reserved",
  "confirmed",
  "canceled",
  "expired",
] as const;
export type VenueBookingStatus = (typeof VENUE_BOOKING_STATUS)[number];

export const VENUE_BOOKING_PAYMENT_STATUS = [
  "pending",
  "paid",
  "failed",
  "refunded",
  "not_required",
  "waived",
] as const;
export type VenueBookingPaymentStatus =
  (typeof VENUE_BOOKING_PAYMENT_STATUS)[number];

export const VENUE_BOOKING_CREATED_VIA = [
  "customer",
  "admin",
  "walk_in",
] as const;
export type VenueBookingCreatedVia = (typeof VENUE_BOOKING_CREATED_VIA)[number];

export const venueBooking = sqliteTable(
  "venue_booking",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),

    // Client-generated idempotency key for safe retries.
    clientBookingId: text("client_booking_id").notNull(),

    // Pointer to the latest payment attempt. Append-only payment rows live in
    // venueBookingPayment — this is just a convenience fk to the active one.
    currentPaymentId: integer("current_payment_id"),

    venueId: integer("venue_id")
      .notNull()
      .references(() => venue.id, { onDelete: "cascade" }),
    experienceId: integer("experience_id")
      .notNull()
      .references(() => venueExperience.id, { onDelete: "cascade" }),

    // THE CUSTOMER the booking is for. Nullable so an anonymous walk-in guest
    // (no hiive account) can be recorded; set to the matched/claimed hiive user
    // when one is linked. Wallet visibility, promo per-user caps, reviews, and
    // social-suggestions all key off this = "the customer", so a walk-in that
    // is NOT a hiive user must have userId = NULL (never the staff member).
    userId: text("user_id").references(() => user.id, { onDelete: "cascade" }),

    // WHO created the booking: the customer for self-service, the staff member
    // for admin / walk-in front-desk bookings. Always set (the migration
    // backfills existing rows from the old userId / walk-in metadata).
    createdByUserId: text("created_by_user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    // "customer" = self-service from the booking page.
    // "admin" = created by a queen on behalf of a real user (has a userId).
    // "walk_in" = front-desk walk-in (guest may be anonymous or a linked user).
    createdVia: text("created_via")
      .notNull()
      .$type<VenueBookingCreatedVia>()
      .default("customer"),

    status: text("status")
      .notNull()
      .$type<VenueBookingStatus>()
      .default("pending"),
    paymentStatus: text("payment_status")
      .notNull()
      .$type<VenueBookingPaymentStatus>()
      .default("pending"),
    paymentProvider: text("payment_provider")
      .notNull()
      .$type<PaymentProvider>()
      .default("razorpay"),

    startTime: integer("start_time", { mode: "timestamp" }).notNull(),
    endTime: integer("end_time", { mode: "timestamp" }).notNull(),

    // For shared_session / entry_pass: number of people attending. For
    // specific_spot: number of named spots reserved in this booking.
    attendeeCount: integer("attendee_count").notNull().default(1),

    pricingSnapshot: text("pricing_snapshot", {
      mode: "json",
    }).$type<PricingSnapshot | null>(),
    totalAmountMinor: integer("total_amount_minor").notNull().default(0),

    // The tax fold, frozen at reserve. Folded once for the whole booking
    // rather than per line: a venue booking is one supply of one experience,
    // so a single rate applies (see venueExperience.taxProfileId).
    //
    // INVARIANT: netAmountMinor + chargeAmountMinor + taxAmountMinor
    //              === totalAmountMinor
    //
    // No discount term here, unlike event_booking_pass: with one fold the
    // discounted total IS the folded amount, so there is nothing to allocate.
    netAmountMinor: integer("net_amount_minor").notNull().default(0),
    chargeAmountMinor: integer("charge_amount_minor").notNull().default(0),
    taxAmountMinor: integer("tax_amount_minor").notNull().default(0),
    taxSnapshot: text("tax_snapshot", {
      mode: "json",
    }).$type<TaxSnapshot | null>(),

    currency: text("currency").notNull().default("INR"),

    expiresAt: integer("expires_at", { mode: "timestamp" }),
    confirmedAt: integer("confirmed_at", { mode: "timestamp" }),
    canceledAt: integer("canceled_at", { mode: "timestamp" }),

    cancellationReason: text("cancellation_reason"),

    // ── Refunds ──────────────────────────────────────────────────────────────
    // The immutable policy version in force when this booking was paid, so a
    // later policy edit can never change the terms it was sold under. Not a FK
    // (see event.refundPolicyId).
    refundPolicyId: integer("refund_policy_id"),
    /** Cumulative amount returned to the member. Guards over-refunding. */
    refundedMinor: integer("refunded_minor").notNull().default(0),
    /** Cumulative amount withheld by policy and kept by the owner. */
    refundFeeMinor: integer("refund_fee_minor").notNull().default(0),
    /**
     * Durable refund progress. Replaces reconstructing state by substring
     * matching sentinels previously stuffed into cancellationReason.
     */
    refundState: text("refund_state")
      .notNull()
      .$type<RefundState>()
      .default("none"),

    // ── Attendance ───────────────────────────────────────────────────────────
    // The signal a venue-run class needs and `event_booking_attendee` has always
    // had. Without it a studio can sell a ten-week course and never record that
    // anybody turned up — which also leaves `hiive_pack.noShowBurnsCredit`
    // authored but unenforceable.
    //
    // Booking-level, because a venue booking has no attendee rows: only
    // `attendeeCount`. `checkedInCount` is therefore how "2 of the 3 arrived"
    // is said, without inventing identities for people never named.
    /** When the booking FIRST reached a checked-in state. Cleared by an undo. */
    checkedInAt: integer("checked_in_at", { mode: "timestamp" }),
    checkedInByUserId: text("checked_in_by_user_id").references(() => user.id, {
      onDelete: "set null",
    }),
    /**
     * 0..attendeeCount. Clamped by `checkInVenueBooking` rather than by a CHECK
     * constraint: adding one to a live bookings table means a full SQLite table
     * rebuild, which is not a trade worth making for an invariant with exactly
     * one writer.
     */
    checkedInCount: integer("checked_in_count").notNull().default(0),

    notes: text("notes"),
    metadata: text("metadata", { mode: "json" }).$type<
      Record<string, unknown>
    >(),

    // Walk-in guest identity, first-class (was previously only in metadata).
    // For a linked walk-in these mirror the userId's contact; for an anonymous
    // guest they are the only identity we hold. walkInGuestPhone is stored
    // normalized (E.164) and is the join key for claim-later on phone verify.
    walkInGuestName: text("walk_in_guest_name"),
    walkInGuestEmail: text("walk_in_guest_email"),
    walkInGuestPhone: text("walk_in_guest_phone"),

    // Reason for an ad-hoc front-desk discount / comp (audit trail). The
    // amount lives in discountAmountMinor + discountSnapshot below.
    manualDiscountReason: text("manual_discount_reason"),

    // Promotions. promotionId is the anchor (limited) promo for indexed
    // redemption counting; the full applied set lives in discountSnapshot.
    promotionId: integer("promotion_id").references(() => promotion.id, {
      onDelete: "set null",
    }),
    promotionCodeId: integer("promotion_code_id").references(
      () => promotionCode.id,
      { onDelete: "set null" },
    ),
    discountAmountMinor: integer("discount_amount_minor").notNull().default(0),
    discountSnapshot: text("discount_snapshot", {
      mode: "json",
    }).$type<PromotionDiscountSnapshot | null>(),
    deviceHash: text("device_hash"),
    paymentFingerprint: text("payment_fingerprint"),

    ...timestamps,
  },
  (t) => [
    // createdByUserId also deduplicates anonymous walk-ins; userId is nullable.
    uniqueIndex("venue_booking_creator_client_booking_unique").on(
      t.createdByUserId,
      t.clientBookingId,
    ),
    uniqueIndex("venue_booking_user_client_booking_unique").on(
      t.userId,
      t.clientBookingId,
    ),
    index("venue_booking_by_venue").on(t.venueId),
    index("venue_booking_by_experience").on(t.experienceId),
    index("venue_booking_by_user").on(t.userId),
    // Discovery affinity reads one user's recent confirmed bookings in time order.
    index("venue_booking_user_status_start_idx").on(
      t.userId,
      t.status,
      t.startTime,
    ),
    // Claim-later lookup: unclaimed walk-ins matched by verified guest phone.
    index("venue_booking_walkin_phone").on(t.walkInGuestPhone),
    index("venue_booking_by_status").on(t.status),
    index("venue_booking_by_venue_time").on(t.venueId, t.startTime, t.endTime),
    index("venue_booking_promo_user_idx").on(t.promotionId, t.userId),
    index("venue_booking_promo_status_idx").on(t.promotionId, t.status),
    index("venue_booking_promo_device_idx").on(t.deviceHash, t.promotionId),
    index("venue_booking_promo_payment_idx").on(
      t.paymentFingerprint,
      t.promotionId,
    ),
    check("venue_booking_start_end_check", sql`${t.startTime} < ${t.endTime}`),
    check("venue_booking_attendee_count_check", sql`${t.attendeeCount} >= 1`),
    index("venue_booking_current_payment_idx").on(t.currentPaymentId),
    // The roster query: one experience's night, arrived or not.
    index("venue_booking_checkin_idx").on(
      t.experienceId,
      t.startTime,
      t.checkedInAt,
    ),
  ],
);

// For specific_spot bookings: which exact spot is reserved.
// For shared_session / entry_pass: empty (capacity is tracked via
// venueBookingSlotLock on the experience).
export const venueBookingSpot = sqliteTable(
  "venue_booking_spot",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    bookingId: integer("booking_id")
      .notNull()
      .references(() => venueBooking.id, { onDelete: "cascade" }),
    spotId: integer("spot_id")
      .notNull()
      .references(() => venueSpot.id, { onDelete: "cascade" }),
    ...timestamps,
  },
  (t) => [
    uniqueIndex("venue_booking_spot_unique").on(t.bookingId, t.spotId),
    index("venue_booking_spot_by_booking").on(t.bookingId),
    index("venue_booking_spot_by_spot").on(t.spotId),
  ],
);

// For entry_pass bookings: which ticket/pass types were reserved.
// Capacity is still tracked by venueBookingSlotLock at the experience level.
export const venueBookingTicket = sqliteTable(
  "venue_booking_ticket",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    bookingId: integer("booking_id")
      .notNull()
      .references(() => venueBooking.id, { onDelete: "cascade" }),
    ticketId: integer("ticket_id")
      .notNull()
      .references(() => venueExperienceTicket.id, { onDelete: "cascade" }),
    quantity: integer("quantity").notNull().default(1),
    unitPriceMinor: integer("unit_price_minor").notNull().default(0),
    currency: text("currency").notNull().default("INR"),
    ...timestamps,
  },
  (t) => [
    uniqueIndex("venue_booking_ticket_unique").on(t.bookingId, t.ticketId),
    index("venue_booking_ticket_by_booking").on(t.bookingId),
    index("venue_booking_ticket_by_ticket").on(t.ticketId),
    check("venue_booking_ticket_quantity_check", sql`${t.quantity} >= 1`),
    check(
      "venue_booking_ticket_unit_price_check",
      sql`${t.unitPriceMinor} >= 0`,
    ),
  ],
);

export const venueBookingAddon = sqliteTable(
  "venue_booking_addon",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    bookingId: integer("booking_id")
      .notNull()
      .references(() => venueBooking.id, { onDelete: "cascade" }),
    addonId: integer("addon_id")
      .notNull()
      .references(() => venueAddon.id, { onDelete: "cascade" }),
    quantity: integer("quantity").notNull().default(1),
    unitPriceMinor: integer("unit_price_minor").notNull().default(0),
    currency: text("currency").notNull().default("INR"),
    ...timestamps,
  },
  (t) => [
    index("venue_booking_addon_by_booking").on(t.bookingId),
    index("venue_booking_addon_by_addon").on(t.addonId),
    check("venue_booking_addon_quantity_check", sql`${t.quantity} >= 1`),
  ],
);

// Slot-level uniqueness primitive. One row per (spot or experience) per grid
// cell covered by a booking. The unique index is the serialization boundary
// that prevents double-booking — two concurrent inserts for the same
// (lockScope, lockKey, slotStart) will fail at the DB.
//
// lockScope describes what the row locks:
//   - "spot":       a specific physical spot. lockKey = spotId. Used for
//                   specific_spot bookings.
//   - "experience": a capacity seat in a shared_session or entry_pass. lockKey
//                   = experienceId. Multiple seats = multiple rows with the
//                   same (lockKey, slotStart), so the unique index is per-seat
//                   via a separate seatIndex column.
export const VENUE_BOOKING_SLOT_LOCK_SCOPE = ["spot", "experience"] as const;
export type VenueBookingSlotLockScope =
  (typeof VENUE_BOOKING_SLOT_LOCK_SCOPE)[number];

export const venueBookingSlotLock = sqliteTable(
  "venue_booking_slot_lock",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    bookingId: integer("booking_id")
      .notNull()
      .references(() => venueBooking.id, { onDelete: "cascade" }),
    venueId: integer("venue_id")
      .notNull()
      .references(() => venue.id, { onDelete: "cascade" }),
    experienceId: integer("experience_id")
      .notNull()
      .references(() => venueExperience.id, { onDelete: "cascade" }),

    lockScope: text("lock_scope").notNull().$type<VenueBookingSlotLockScope>(),
    lockKey: integer("lock_key").notNull(),
    seatIndex: integer("seat_index").notNull().default(0),

    slotStart: integer("slot_start", { mode: "timestamp" }).notNull(),
    /**
     * Exclusive end of the held window.
     *
     * Without it a lock was a point, not an interval, and two overlapping
     * bookings on one spot (10:00-11:00 and 10:30-11:30 — both valid slots for a
     * `flexible_time` experience) landed on different keys and both succeeded.
     * Availability is an interval overlap against this column.
     */
    slotEnd: integer("slot_end", { mode: "timestamp" }).notNull(),
    ...timestamps,
  },
  (t) => [
    // Cheap guard against an exact duplicate. It cannot express "no interval
    // overlaps this one", so the engine re-checks the overlap inside the write
    // transaction before inserting.
    uniqueIndex("venue_booking_slot_lock_unique").on(
      t.lockScope,
      t.lockKey,
      t.slotStart,
      t.seatIndex,
    ),
    index("venue_booking_slot_lock_by_booking").on(t.bookingId),
    index("venue_booking_slot_lock_by_venue_time").on(t.venueId, t.slotStart),
    index("venue_booking_slot_lock_by_experience_time").on(
      t.experienceId,
      t.slotStart,
    ),
    index("venue_booking_slot_lock_by_scope_window").on(
      t.lockScope,
      t.lockKey,
      t.slotStart,
      t.slotEnd,
    ),
  ],
);

// Append-only payment attempts. Provider identifiers live here to avoid drift.
export const venueBookingPayment = sqliteTable(
  "venue_booking_payment",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    bookingId: integer("booking_id")
      .notNull()
      .references(() => venueBooking.id, { onDelete: "cascade" }),

    idempotencyKey: text("idempotency_key").notNull(),

    provider: text("provider").notNull().$type<PaymentProvider>(),
    status: text("status").notNull().$type<PaymentStatus>().default("pending"),

    amountMinor: integer("amount_minor").notNull().default(0),
    currency: text("currency").notNull().default("INR"),

    orderId: text("order_id"),
    paymentId: text("payment_id"),
    refundId: text("refund_id"),

    metadata: text("metadata", { mode: "json" }).$type<
      Record<string, unknown>
    >(),

    ...timestamps,
  },
  (t) => [
    uniqueIndex("venue_booking_payment_booking_idempotency_unique").on(
      t.bookingId,
      t.idempotencyKey,
    ),
    uniqueIndex("venue_booking_payment_provider_order_unique").on(
      t.provider,
      t.orderId,
    ),
    uniqueIndex("venue_booking_payment_provider_payment_unique").on(
      t.provider,
      t.paymentId,
    ),
    uniqueIndex("venue_booking_payment_provider_refund_unique").on(
      t.provider,
      t.refundId,
    ),
    index("venue_booking_payment_by_booking").on(t.bookingId),
    index("venue_booking_payment_provider_idx").on(t.provider),
    index("venue_booking_payment_status_idx").on(t.status),
  ],
);

export type VenueBooking = typeof venueBooking.$inferSelect;
export type VenueBookingInsert = typeof venueBooking.$inferInsert;
export type VenueBookingSpot = typeof venueBookingSpot.$inferSelect;
export type VenueBookingSpotInsert = typeof venueBookingSpot.$inferInsert;
export type VenueBookingAddon = typeof venueBookingAddon.$inferSelect;
export type VenueBookingAddonInsert = typeof venueBookingAddon.$inferInsert;
export type VenueBookingSlotLock = typeof venueBookingSlotLock.$inferSelect;
export type VenueBookingSlotLockInsert =
  typeof venueBookingSlotLock.$inferInsert;
export type VenueBookingPayment = typeof venueBookingPayment.$inferSelect;
export type VenueBookingPaymentInsert = typeof venueBookingPayment.$inferInsert;
